/**
 * Audit Logs Tab Component
 *
 * Comprehensive audit log viewer with:
 * - User filtering
 * - Session filtering (dynamic based on user)
 * - Date range filtering
 * - Log level filtering
 * - Pagination
 * - Separate views for Audit Logs and WebSocket Events
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getAuditLogs,
  getWebSocketEvents,
  getAuditUsernames,
  getAuditSessions,
  type AuditLogFilters,
  type WebSocketEventFilters,
} from '@/services/adminApi';
import type { AuditLog, WebSocketEvent } from '@/types/user';

type ViewType = 'logs' | 'websocket';

const ITEMS_PER_PAGE = 50;

export default function AuditLogsTab() {
  const { t } = useTranslation('admin');
  // View state
  const [view, setView] = useState<ViewType>('logs');

  // Data state
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [events, setEvents] = useState<WebSocketEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Filter state
  const [levelFilter, setLevelFilter] = useState('');
  const [usernameFilter, setUsernameFilter] = useState('');
  const [sessionFilter, setSessionFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Dropdown data
  const [usernames, setUsernames] = useState<string[]>([]);
  const [sessions, setSessions] = useState<string[]>([]);

  // Load usernames on mount
  useEffect(() => {
    const loadUsernames = async () => {
      try {
        const data = await getAuditUsernames();
        setUsernames(data);
      } catch (err) {
        console.error('Failed to load usernames:', err);
      }
    };
    loadUsernames();
  }, []);

  // Load sessions when username changes
  useEffect(() => {
    const loadSessions = async () => {
      try {
        const data = await getAuditSessions(usernameFilter || undefined);
        setSessions(data);
        // Reset session filter if it's not in the new list
        if (sessionFilter && !data.includes(sessionFilter)) {
          setSessionFilter('');
        }
      } catch (err) {
        console.error('Failed to load sessions:', err);
      }
    };
    loadSessions();
  }, [usernameFilter]);

  // Fetch data when filters or pagination change
  useEffect(() => {
    if (view === 'logs') {
      fetchLogs();
    } else {
      fetchEvents();
    }
  }, [view, currentPage, levelFilter, usernameFilter, sessionFilter, startDate, endDate]);

  const fetchLogs = async () => {
    setIsLoading(true);
    setError('');
    try {
      const filters: AuditLogFilters = {
        limit: ITEMS_PER_PAGE,
        offset: (currentPage - 1) * ITEMS_PER_PAGE,
      };

      if (levelFilter) filters.level = levelFilter;
      if (usernameFilter) filters.username = usernameFilter;
      if (sessionFilter) filters.session_id = sessionFilter;
      if (startDate) filters.start_date = new Date(startDate).toISOString();
      if (endDate) filters.end_date = new Date(endDate).toISOString();

      const data = await getAuditLogs(filters);
      setLogs(data.logs);
      setTotalItems(data.total);
    } catch (err: any) {
      setError(err.message || 'Failed to load audit logs');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchEvents = async () => {
    setIsLoading(true);
    setError('');
    try {
      const filters: WebSocketEventFilters = {
        limit: ITEMS_PER_PAGE,
        offset: (currentPage - 1) * ITEMS_PER_PAGE,
      };

      if (usernameFilter) filters.username = usernameFilter;
      if (sessionFilter) filters.session_id = sessionFilter;
      if (startDate) filters.start_date = new Date(startDate).toISOString();
      if (endDate) filters.end_date = new Date(endDate).toISOString();

      const data = await getWebSocketEvents(filters);
      setEvents(data.events);
      setTotalItems(data.total);
    } catch (err: any) {
      setError(err.message || 'Failed to load WebSocket events');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetFilters = () => {
    setLevelFilter('');
    setUsernameFilter('');
    setSessionFilter('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  const handleViewChange = (newView: ViewType) => {
    setView(newView);
    setCurrentPage(1);
    // Keep filters when switching views
  };

  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const getLevelColor = (level: string) => {
    const colors: Record<string, string> = {
      ERROR: 'text-red-600 bg-red-50',
      CRITICAL: 'text-red-800 bg-red-100',
      WARNING: 'text-yellow-600 bg-yellow-50',
      INFO: 'text-blue-600 bg-blue-50',
      DEBUG: 'text-gray-600 bg-gray-50',
    };
    return colors[level] || 'text-gray-600 bg-gray-50';
  };

  const getHttpMethodColor = (method: string | null) => {
    if (!method) return '';
    const colors: Record<string, string> = {
      GET: 'text-green-700 bg-green-100',
      POST: 'text-blue-700 bg-blue-100',
      PUT: 'text-orange-700 bg-orange-100',
      PATCH: 'text-yellow-700 bg-yellow-100',
      DELETE: 'text-red-700 bg-red-100',
    };
    return colors[method.toUpperCase()] || 'text-gray-600 bg-gray-100';
  };

  const getHttpStatusColor = (status: number | null) => {
    if (!status) return '';
    if (status >= 200 && status < 300) return 'text-green-700 bg-green-100';
    if (status >= 300 && status < 400) return 'text-blue-700 bg-blue-100';
    if (status >= 400 && status < 500) return 'text-orange-700 bg-orange-100';
    if (status >= 500) return 'text-red-700 bg-red-100';
    return 'text-gray-600 bg-gray-100';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <div>
      {/* Header with View Toggle */}
      <div className="mb-6 flex justify-between items-center">
        <div className="flex gap-2">
          <button
            onClick={() => handleViewChange('logs')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              view === 'logs'
                ? 'bg-black text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
            }`}
          >
            {t('auditLogs.auditLogs')}
          </button>
          <button
            onClick={() => handleViewChange('websocket')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              view === 'websocket'
                ? 'bg-black text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
            }`}
          >
            {t('auditLogs.websocketEvents')}
          </button>
        </div>

        <div className="text-sm text-gray-600">
          {t('auditLogs.totalRecords')} {totalItems.toLocaleString()} {t('auditLogs.records')}
        </div>
      </div>

      {/* Comprehensive Filters */}
      <div className="mb-6 bg-white p-4 rounded-lg shadow space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-sm font-semibold text-gray-700">{t('auditLogs.filters')}</h3>
          <button
            onClick={handleResetFilters}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            {t('auditLogs.resetAll')}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* User Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('auditLogs.user')}</label>
            <select
              value={usernameFilter}
              onChange={(e) => {
                setUsernameFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="">{t('auditLogs.allUsers')}</option>
              {usernames.map((username) => (
                <option key={username} value={username}>
                  {username}
                </option>
              ))}
            </select>
          </div>

          {/* Session Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('auditLogs.session')}</label>
            <select
              value={sessionFilter}
              onChange={(e) => {
                setSessionFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              disabled={sessions.length === 0}
            >
              <option value="">{t('auditLogs.allSessions')}</option>
              {sessions.map((session) => (
                <option key={session} value={session}>
                  {session.substring(0, 8)}...
                </option>
              ))}
            </select>
          </div>

          {/* Log Level Filter (only for audit logs) */}
          {view === 'logs' && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('auditLogs.level')}</label>
              <select
                value={levelFilter}
                onChange={(e) => {
                  setLevelFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="">{t('auditLogs.allLevels')}</option>
                <option value="ERROR">{t('auditLogs.error')}</option>
                <option value="WARNING">{t('auditLogs.warning')}</option>
                <option value="INFO">{t('auditLogs.info')}</option>
                <option value="DEBUG">{t('auditLogs.debug')}</option>
              </select>
            </div>
          )}

          {/* Start Date Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('auditLogs.startDate')}</label>
            <input
              type="datetime-local"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>

          {/* End Date Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('auditLogs.endDate')}</label>
            <input
              type="datetime-local"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
        </div>

        {/* Active Filters Summary */}
        {(levelFilter || usernameFilter || sessionFilter || startDate || endDate) && (
          <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-200">
            <span className="text-xs text-gray-500">{t('auditLogs.activeFilters')}</span>
            {levelFilter && (
              <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                Level: {levelFilter}
              </span>
            )}
            {usernameFilter && (
              <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
                User: {usernameFilter}
              </span>
            )}
            {sessionFilter && (
              <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded">
                Session: {sessionFilter.substring(0, 8)}...
              </span>
            )}
            {startDate && (
              <span className="px-2 py-1 text-xs bg-orange-100 text-orange-800 rounded">
                From: {new Date(startDate).toLocaleDateString()}
              </span>
            )}
            {endDate && (
              <span className="px-2 py-1 text-xs bg-orange-100 text-orange-800 rounded">
                To: {new Date(endDate).toLocaleDateString()}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md text-red-800">
          {error}
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-500">{t('auditLogs.loading')}</p>
        </div>
      )}

      {/* Audit Logs View */}
      {!isLoading && view === 'logs' && (
        <div className="space-y-3">
          {logs.map((log) => (
            <div key={log.id} className="bg-white p-4 rounded-lg shadow hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    {/* Log Level Badge */}
                    <span className={`px-2 py-1 text-xs font-medium rounded ${getLevelColor(log.level)}`}>
                      {log.level}
                    </span>

                    {/* HTTP Method Badge */}
                    {log.http_method && (
                      <span className={`px-2 py-1 text-xs font-medium rounded ${getHttpMethodColor(log.http_method)}`}>
                        {log.http_method}
                      </span>
                    )}

                    {/* HTTP Status Badge */}
                    {log.http_status && (
                      <span className={`px-2 py-1 text-xs font-medium rounded ${getHttpStatusColor(log.http_status)}`}>
                        {log.http_status}
                      </span>
                    )}

                    {/* Component Badge */}
                    {log.component && (
                      <span className="px-2 py-1 text-xs font-medium rounded text-purple-700 bg-purple-100">
                        {log.component}
                      </span>
                    )}

                    {/* Category */}
                    {log.category && (
                      <span className="text-xs text-gray-500">{log.category}</span>
                    )}

                    {/* Username */}
                    {log.username && (
                      <span className="text-xs font-medium text-gray-700 bg-gray-100 px-2 py-1 rounded">
                        👤 {log.username}
                      </span>
                    )}

                    {/* Session ID */}
                    {log.session_id && (
                      <span className="text-xs text-gray-500 font-mono">
                        Session: {log.session_id.substring(0, 8)}
                      </span>
                    )}

                    {/* Endpoint */}
                    {log.endpoint && (
                      <span className="text-xs text-gray-600">
                        📍 {log.endpoint}
                      </span>
                    )}

                    {/* IP Address */}
                    {log.ip_address && (
                      <span className="text-xs text-gray-500 font-mono">
                        🌐 {log.ip_address}
                      </span>
                    )}
                  </div>

                  {/* Message */}
                  <p className="text-sm text-gray-900 mb-2 font-mono">{log.message}</p>

                  {/* Timestamp and Duration */}
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>🕐 {formatDate(log.timestamp)}</span>
                    {log.duration_ms !== null && log.duration_ms !== undefined && (
                      <span>⏱️ {log.duration_ms}ms</span>
                    )}
                  </div>

                  {/* Error Trace (from backend exceptions) */}
                  {log.error_trace && (
                    <details className="mt-2">
                      <summary className="text-xs text-red-600 cursor-pointer font-medium">
                        📋 Stack Trace
                      </summary>
                      <pre className="mt-2 text-xs bg-red-50 p-3 rounded overflow-x-auto border border-red-200 font-mono whitespace-pre-wrap">
                        {log.error_trace}
                      </pre>
                    </details>
                  )}

                  {/* Request Body */}
                  {log.request_body && (
                    <details className="mt-2">
                      <summary className="text-xs text-orange-600 cursor-pointer font-medium">
                        📤 Request Body
                      </summary>
                      <pre className="mt-2 text-xs bg-orange-50 p-3 rounded overflow-x-auto border border-orange-200 font-mono whitespace-pre-wrap">
                        {(() => {
                          try {
                            return JSON.stringify(JSON.parse(log.request_body), null, 2);
                          } catch {
                            return log.request_body;
                          }
                        })()}
                      </pre>
                    </details>
                  )}

                  {/* Details/Context */}
                  {log.details && Object.keys(log.details).length > 0 && (
                    <details className="mt-2">
                      <summary className="text-xs text-blue-600 cursor-pointer font-medium">
                        ℹ️ Details
                      </summary>
                      <pre className="mt-2 text-xs bg-blue-50 p-3 rounded overflow-x-auto border border-blue-200 font-mono whitespace-pre-wrap">
                        {JSON.stringify(log.details, null, 2)}
                      </pre>
                    </details>
                  )}

                  {/* User Agent */}
                  {log.user_agent && (
                    <details className="mt-2">
                      <summary className="text-xs text-gray-500 cursor-pointer">
                        🔧 User Agent
                      </summary>
                      <p className="mt-1 text-xs text-gray-600 font-mono break-all">
                        {log.user_agent}
                      </p>
                    </details>
                  )}
                </div>
              </div>
            </div>
          ))}
          {logs.length === 0 && (
            <div className="text-center py-12 bg-white rounded-lg shadow">
              <p className="text-gray-500">{t('auditLogs.noLogsFound')}</p>
            </div>
          )}
        </div>
      )}

      {/* WebSocket Events View */}
      {!isLoading && view === 'websocket' && (
        <div className="space-y-3">
          {events.map((event) => (
            <div key={event.id} className="bg-white p-4 rounded-lg shadow hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="px-2 py-1 text-xs font-medium rounded bg-purple-100 text-purple-800">
                      {event.event_type}
                    </span>
                    {event.direction && (
                      <span className="text-xs text-gray-500">
                        {event.direction === 'outgoing' ? '📤' : '📥'} {event.direction}
                      </span>
                    )}
                    {event.username && (
                      <span className="text-xs font-medium text-gray-700 bg-gray-100 px-2 py-1 rounded">
                        👤 {event.username}
                      </span>
                    )}
                    {event.session_id && (
                      <span className="text-xs text-gray-500 font-mono">
                        Session: {event.session_id.substring(0, 8)}
                      </span>
                    )}
                  </div>
                  {event.message_type && (
                    <p className="text-sm text-gray-900 mb-2">
                      <span className="font-medium">Type:</span> {event.message_type}
                    </p>
                  )}
                  <div className="text-xs text-gray-500">
                    🕐 {formatDate(event.timestamp)}
                  </div>
                  {event.error_message && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                      <p className="text-sm text-red-600 font-medium">⚠️ Error: {event.error_message}</p>
                    </div>
                  )}
                  {event.message_data && (
                    <details className="mt-2">
                      <summary className="text-xs text-blue-600 cursor-pointer font-medium">
                        📦 Message Data
                      </summary>
                      <pre className="mt-2 text-xs bg-blue-50 p-3 rounded overflow-x-auto border border-blue-200 font-mono whitespace-pre-wrap">
                        {typeof event.message_data === 'object'
                          ? JSON.stringify(event.message_data, null, 2)
                          : event.message_data}
                      </pre>
                    </details>
                  )}
                  {event.error_trace && (
                    <details className="mt-2">
                      <summary className="text-xs text-red-600 cursor-pointer font-medium">
                        📋 Error Trace
                      </summary>
                      <pre className="mt-2 text-xs bg-red-50 p-3 rounded overflow-x-auto border border-red-200 font-mono">
                        {event.error_trace}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
            </div>
          ))}
          {events.length === 0 && (
            <div className="text-center py-12 bg-white rounded-lg shadow">
              <p className="text-gray-500">{t('auditLogs.noEventsFound')}</p>
            </div>
          )}
        </div>
      )}

      {/* Pagination Controls */}
      {!isLoading && totalItems > 0 && (
        <div className="mt-6 flex justify-between items-center bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-600">
            {t('auditLogs.showing')} {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, totalItems)} {t('auditLogs.of')} {totalItems.toLocaleString()}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                currentPage === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              ← {t('auditLogs.previous')}
            </button>

            <div className="flex items-center gap-1">
              {/* Show first page */}
              {currentPage > 3 && (
                <>
                  <button
                    onClick={() => setCurrentPage(1)}
                    className="px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50"
                  >
                    1
                  </button>
                  {currentPage > 4 && <span className="px-2 text-gray-500">...</span>}
                </>
              )}

              {/* Show pages around current */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = Math.max(1, currentPage - 2) + i;
                if (pageNum > totalPages) return null;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-2 text-sm rounded-md transition-colors ${
                      pageNum === currentPage
                        ? 'bg-black text-white'
                        : 'border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              }).filter(Boolean)}

              {/* Show last page */}
              {currentPage < totalPages - 2 && (
                <>
                  {currentPage < totalPages - 3 && <span className="px-2 text-gray-500">...</span>}
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    className="px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50"
                  >
                    {totalPages}
                  </button>
                </>
              )}
            </div>

            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                currentPage === totalPages
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {t('auditLogs.next')} →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
